import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField, Tabs, Tab, IconButton, Tooltip
} from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';


import { get, post } from "../fetchers/fetchers";
import { IState, IImplementation } from "../types/types";
import { API_URL } from "../env";
import Implementation from './implemenation';

interface ImplementationProps {
  open: boolean;
  onClose: () => void;
  assessmentId: number;
  parentState: IState | undefined,
  states: IState[]
}


const ImplementationDialog: React.FC<ImplementationProps> = ({
  open,
  onClose,
  assessmentId,
  parentState
}) => {

  const [loaded, setLoaded] = useState<boolean>(false);
  const [implementations, setImplementations] = useState<IImplementation[]>([]);
  const [createImplementationDlg, setCreateImplementationDlg] = useState<boolean>(false);
  const [implementationName, setImplementationName] = useState<string>("");
  const [currentImplementation, setCurrentImplementation] = useState<IImplementation | undefined>(undefined);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [editingImplementation, setEditingImplementation] = useState<IImplementation | null>(null);
  const [newImplementationName, setNewImplementationName] = useState("");
  const [removeDialogOpen, setRemoveDialogOpen] = useState<boolean>(false);
  const [implementationToRemove, setImplementationToRemove] = useState<IImplementation | null>(null);

  useEffect(() => {
    if (open) {

      setLoaded(false);
    }

  }, [open])

  useEffect(() => {
    if (!parentState) return;
    if (loaded) return;
    loadImplementations().then((imps) => {

    }).then(() => {
      setLoaded(true);
    })


  }, [parentState, loaded, open]);

  const toggleCurrentImplementation = async (selectedImplementation: IImplementation) => {
    // If the selected implementation is already current, no need to change anything
    // if (selectedImplementation.is_current) return;

    // Set the selected implementation as current and others as not current
    const updatedImplementations = implementations.map(impl => ({
      ...impl,
      is_current: impl.id === selectedImplementation.id
    }));

    // remove pathways from updated implementation


    // Update the local state
    setImplementations(updatedImplementations);

    // Update the server
    // Note: This assumes that your API allows batch updating of implementations.
    // If your API doesn't support batch updating, you'll need to update each implementation individually.
    const promises = updatedImplementations.map(impl => {
      const imp = { ...impl }
      delete imp.pathways;
      return post<IImplementation>(API_URL + "implementation/" + assessmentId + "/", imp);
    });
    await Promise.all(promises);

    console.log("toggleCurrentImplementation");

    // Reload the data or handle the UI update accordingly
    setLoaded(false);
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setCurrentTab(newValue);
  };

  const callbackReload = () => {
    setLoaded(false);
  }

  const promptRemoveImplementation = (implementation: IImplementation) => {
    setImplementationToRemove(implementation);
    setRemoveDialogOpen(true);
  }

  const handleRemoveDialogClose = () => {
    setRemoveDialogOpen(false);
    setImplementationToRemove(null);
  }

  const confirmRemoveImplementation = async () => {
    if (!implementationToRemove) {
      return;
    }
    await handleRemoveImplementation(implementationToRemove);
    handleRemoveDialogClose();
  }

  const handleRemoveImplementation = async (implementation: IImplementation) => {
    if (!implementation.id) {
      return;
    }

    const updatedImplementation = {
      ...implementation,
      is_active: false,
      is_current: false
    };
    delete updatedImplementation.pathways;

    await post<IImplementation>(API_URL + "implementation/" + assessmentId + "/", updatedImplementation);

    const remainingImplementations = implementations.filter(impl => impl.id !== implementation.id);
    setImplementations(remainingImplementations);

    if (remainingImplementations.length === 0) {
      setCurrentImplementation(undefined);
      setCurrentTab(0);
    } else {
      const newTabIndex = Math.min(currentTab, remainingImplementations.length - 1);
      setCurrentTab(newTabIndex);
      setCurrentImplementation(remainingImplementations[newTabIndex]);
    }

    setLoaded(false);
  }

  const loadImplementations = async () => {
    const d = await get<IImplementation[]>(API_URL + "implementation/" + assessmentId + "/");

    // filter implementations to state and only include active entries
    let filtered_d = d.filter(i => i.state_uuid === parentState?.uuid && i.is_active !== false);

    setImplementations(filtered_d);

    if (filtered_d.length > 0) {
      const newTabIndex = Math.min(currentTab, filtered_d.length - 1);
      setCurrentTab(newTabIndex);
      setCurrentImplementation(filtered_d[newTabIndex]);
    } else {
      setCurrentImplementation(undefined);
      setCurrentTab(0);
    }

    return filtered_d;
  }

  const postImplementation = async (implementation: IImplementation) => {

    const imp = { ...implementation }

    // remove pathways from updated implementation
    delete imp.pathways;

    const d = await post<IImplementation>(API_URL + "implementation/" + assessmentId + "/", imp);
    setLoaded(false);
  }

  // recusive function to get all the children of a state
  const getChildren = (state: IState, states: IState[]) => {
    let children = states.filter(s => s.parent_state_uuid === state.uuid);
    let result: IState[] = [];
    children.forEach(c => {
      result.push(c);
      result = result.concat(getChildren(c, states));
    }
    )
    return result;
  }

  if (!parentState) {
    return <div></div>
  }


  const onCloseHandler = () => {
    onClose();
    setLoaded(false);
  }


  const handleCreateImplemenation = () => {
    setCreateImplementationDlg(true);
  }

  const handleEditImplementation = (implementation: IImplementation) => {
    setEditingImplementation(implementation);
    setNewImplementationName(implementation.name);
  };

  const handleEditImplementationClose = () => {
    setEditingImplementation(null);
    setNewImplementationName("");
  };

  const handleEditImplementationSave = async () => {
    if (editingImplementation) {
      const updatedImplementation = {
        ...editingImplementation,
        name: newImplementationName
      };

      postImplementation(updatedImplementation);
      handleEditImplementationClose();
      setLoaded(false);
    }
  };

  const editImplementationDialog = () => (
    <Dialog open={Boolean(editingImplementation)} onClose={handleEditImplementationClose} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">Edit Implementation Name</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="name"
          label="Implementation Name"
          type="text"
          fullWidth
          value={newImplementationName}
          onChange={(e) => setNewImplementationName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleEditImplementationClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleEditImplementationSave} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
  ;


  const handleCreateImplementationClose = () => {
    setCreateImplementationDlg(false);
  }

  const handleCreateImplementationCreate = () => {
    let implementation: IImplementation = {
      assessment_project_id: assessmentId,
      name: implementationName,
      state_uuid: parentState?.uuid,
      is_current: true,
      is_active: true
    }

    postImplementation(implementation);
    setCreateImplementationDlg(false);
  }

  const createImplementationDialog = () => {

    return (
      <Dialog open={createImplementationDlg} onClose={handleCreateImplementationClose} aria-labelledby="form-dialog-title" maxWidth="lg">
        <DialogTitle id="form-dialog-title">Create Implementation for {parentState?.name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Name"
            type="text"
            fullWidth
            onChange={e => setImplementationName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateImplementationClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleCreateImplementationCreate} color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  const removeImplementationDialog = () => {
    return (
      <Dialog open={removeDialogOpen} onClose={handleRemoveDialogClose} aria-labelledby="remove-implementation-dialog-title" maxWidth="xs" fullWidth>
        <DialogTitle id="remove-implementation-dialog-title">Remove Implementation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove {implementationToRemove?.name ?? "this implementation"}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmRemoveImplementation} color="secondary">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  const LabelWithIcon: React.FC<{ implementation: IImplementation }> = ({ implementation }) => {
    return (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={implementation.is_current ? "Current implementation" : "Set as current"}>
          <IconButton onClick={(event) => { event.stopPropagation(); toggleCurrentImplementation(implementation); }} style={{ padding: '0', marginRight: '8px' }}>
            {implementation.is_current ? <StarIcon color="secondary" /> : <StarBorderIcon />}
          </IconButton>
        </Tooltip>
        {implementation.name}
        <Tooltip title="Edit implementation name">
          <IconButton onClick={(event) => { event.stopPropagation(); handleEditImplementation(implementation); }} style={{ padding: '0', marginLeft: '8px' }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove implementation">
          <IconButton onClick={(event) => { event.stopPropagation(); promptRemoveImplementation(implementation); }} style={{ padding: '0', marginLeft: '8px' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </span>
    );
  };

  return (
    <>
      {createImplementationDialog()}
      {editImplementationDialog()}
      {removeImplementationDialog()}
      <Dialog open={open} onClose={onCloseHandler} aria-labelledby="form-dialog-title" maxWidth="lg">
        <DialogTitle id="form-dialog-title">Implementation for {parentState?.name}</DialogTitle>
        <DialogContent>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Implementation Tabs">
            {implementations.map((implementation, index) => (
              <Tab
                label={<LabelWithIcon implementation={implementation} />}
                key={index}
              />
            ))}
            <Tab label="+" onClick={handleCreateImplemenation} />
          </Tabs>

          {implementations.map((implementation, index) => (
            <div
              role="tabpanel"
              hidden={currentTab !== index}
              id={`tabpanel-${index}`}
              aria-labelledby={`tab-${index}`}
            >
              {currentTab === index && (
                <Implementation
                  assessmentId={assessmentId}
                  parentState={parentState}
                  implementation_id={implementation.id || 0}
                  callbackReload={callbackReload}
                />
              )}
            </div>
          ))}

        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

};

export default ImplementationDialog;
